import { Box, CircularProgress, Stack, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import ActionCard from "../components/common/ActionCard";
import { Icons, Images } from "../models/assets-files";
import { ProductRequest, ProductRequestStatusEnum } from "../models/product.models";
import { ImageService } from "../services/image.service";
import { UserService } from "../services/user.service";
import PageBanner from "../components/application/PageBanner";
import { BannerTexts } from "../data/texts";
import { useSelector } from "react-redux";
import { StoreState } from "../store/store";
import ProductsTable from "../components/client-dashboard/product-request-table/ProductRequestTable";
import ProductsAndServices from "../components/application/ProductsAndServices";
import useScreen from "../components/common/UseScreen";
import { PaginationModel } from "../components/common/CustomTable";
import { useNavigate } from "react-router-dom";
import { GridSortModel } from "@mui/x-data-grid";
interface RequestAmount {
  [ProductRequestStatusEnum.DONE]: number;
  [ProductRequestStatusEnum.IN_PROGRESS]: number;
  [ProductRequestStatusEnum.PENDING]: number;
  [ProductRequestStatusEnum.WAITING_FOR_ACTIONS]: number;
}

interface ClientDashboardPageProps {
  onProductSelect: (href: string) => void;
}

const ClientDashboardPage = ({ onProductSelect }: ClientDashboardPageProps) => {
  const theme = useTheme();
  const { isDesktop } = useScreen();
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const actionCardBreakpoint = useMediaQuery(theme.breakpoints.up(1100));
  const navigate = useNavigate();
  const user = useSelector((store: StoreState) => store.user.loggedInUser);
  const [requests, setRequests] = useState<ProductRequest>();
  const initialRequestAmount: RequestAmount = {
    [ProductRequestStatusEnum.DONE]: 0,
    [ProductRequestStatusEnum.IN_PROGRESS]: 0,
    [ProductRequestStatusEnum.PENDING]: 0,
    [ProductRequestStatusEnum.WAITING_FOR_ACTIONS]: 0,
  };
  const [requestAmount, setRequestAmount] = useState<RequestAmount>(initialRequestAmount);

  useEffect(() => {
    setIsLoading(true);
    initProductRequests();
  }, [page]);

  useEffect(() => {
    if (requests) {
      setInitialRequestAmount();
    }
  }, [requests]);

  const initProductRequests = async () => {
    try {
      const fetchedRequests = await UserService.getUserProductRequests(page, 7);

      if (fetchedRequests) {
        setIsLoading(false);
        setRequests(fetchedRequests);
      }
    } catch (error) {
      setIsLoading(false);
      console.log("Error in ClientDashboardPage: ", error);
    }
  };

  const setInitialRequestAmount = () => {
    const requestAmountMap = { ...initialRequestAmount };

    if (requests?.data && Array.isArray(requests.data)) {
      requests.data.forEach((request) => {
        if (request.status === ProductRequestStatusEnum.REJECTED) return;
        requestAmountMap[request.status]++;
      });
    }

    setRequestAmount(requestAmountMap);
  };

  const onPagination = (model: PaginationModel) => {
    setPage(model.page + 1);
  };

  const onTableRowClick = (params: any) => {
    const { productName } = params.row;
    let formattedProductName = productName.camelCaseToKebabCase();
    switch (formattedProductName) {
      case "blockchain-analysis": {
        formattedProductName += "/status";
        break;
      }
      case "web-intelligence":
      case "correspondence": {
        formattedProductName = `product/${formattedProductName}`;
        break;
      }

      case "exchange-dispute":
      case "bank-dispute":
        return;
    }
    navigate(formattedProductName);
  };
  const onSortColumn = async (model: GridSortModel) => {
    setIsLoading(true);
    try {
      const requests = await UserService.getUserProductRequests(page, 7, null, null, null, model);
      if (requests) {
        setRequests(requests);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      {isLoading && <CircularProgress sx={{ position: "absolute", left: "50%", top: "50%" }} />}
      <Stack
        sx={{
          opacity: isLoading ? "0.3" : "1",
          pointerEvents: isLoading ? "none" : "auto",
          padding: isDesktop ? "24px" : "0px",
          background: theme.palette.background.default,
        }}
      >
        <PageBanner
          containerSx={{ marginBottom: "30px" }}
          height="274px"
          imageSrc={ImageService.getLocalImgPath(Images.Folders.BANNERS, Images.Banners.LAPTOP_BIG, "png")}
          title={`${BannerTexts.WELCOME_BACK} ${user?.fullName}`}
          subtitle={BannerTexts.THANK_YOU_BACK}
        />
        <Stack direction={"row"} gap={"24px"} sx={{ marginBottom: "30px" }} flexWrap={!actionCardBreakpoint ? "wrap" : "nowrap"}>
          {Object.keys(requestAmount).map((key) => {
            key = key as keyof RequestAmount;
            const type = key as ProductRequestStatusEnum;
            if (type === ProductRequestStatusEnum.REJECTED) return null;
            return (
              <ActionCard
                containerSx={{
                  width: actionCardBreakpoint ? "calc(25% - 12px)" : "calc(50% - 12px)",
                  boxShadow: "0px 2px 6px 0px #4C4E641A",
                }}
                subIconText={`${requests?.statusCount ? requests.statusCount[type] : 0}`}
                key={key}
                title={key}
                imageSrc={ImageService.getLocalIconPath(Icons.Folders.PRODUCT_STATUS, key.camelCaseToKebabCase())}
              />
            );
          })}
        </Stack>
        {requests && (
          <Box
            sx={{
              marginBottom: "20px",
              paddingInline: isDesktop ? "" : "10px",
            }}
          >
            <ProductsTable onRowClick={onTableRowClick} onSortColumn={onSortColumn} onPagination={onPagination} page={page - 1} requests={requests} />
          </Box>
        )}
        <ProductsAndServices onProductSelect={onProductSelect} />
      </Stack>
    </Box>
  );
};

export default ClientDashboardPage;
